import { LinearProgress, Paper, Typography } from '@mui/material';
import { FieldTemplate, FormTemplate, Visit } from 'api/models';
import {
  FieldImportMeta,
  Form as FormModel,
  FormStatus,
} from 'api/models/Form';
import { getInitialFormValues } from 'forms/Form';
import { query } from 'operations';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  formTemplateState,
  selectedFormTemplateTypeState,
  selectedPatientState,
} from 'state';
import { GeneralIds, ModelTypes } from 'types/types';
import FormLayout from './FormLayout';
import useRequest from 'hooks/useRequest';
import { FormTemplateStatus } from 'api/models/FormTemplate';
import {
  commentsAtom,
  formLayoutItemsState,
  formsFieldTemplates,
  lastTimeFormWasSaved,
} from 'state/formState';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { Box } from '@mui/system';
import LastSavedIndicator from 'components/LastSavedIndicator/LastSavedIndicator';
import { getFormsComments } from 'api';
import {
  insertItemsForRepeatingFields,
  insertItemsForRepeatingFieldsInRepeatingLayoutItems,
  insertLayoutItemsForRepeatingFields,
} from './utils';

interface FormParams {
  mode: 'create' | 'edit' | 'view';
}

interface FormData {
  visit: Visit;
  formTemplate: FormTemplate;
  fieldTemplates: FieldTemplate[];
  initialValues: {
    [key: string]: string | number | boolean | string[] | null;
  };
  formStatus: FormStatus;
  externalFieldValues: {
    [key: string]: string | number | boolean | string[] | null;
  };
  importedFieldMap?: Map<string, FieldImportMeta>;
}

export default function Form({ mode }: FormParams): JSX.Element {
  const isReadOnly = mode === 'view';
  const { projectId, formTemplateTypeId, visitId, formId, patientId } =
    useParams<GeneralIds>();
  const formTempalteType = useRecoilValue(selectedFormTemplateTypeState);
  const patient = useRecoilValue(selectedPatientState);
  const setFormsFieldTemplates = useSetRecoilState(formsFieldTemplates);
  const setFormTemplate = useSetRecoilState(formTemplateState);
  const lastSaved = useRecoilValue(lastTimeFormWasSaved);
  const setComments = useSetRecoilState(commentsAtom);
  const setLayoutItems = useSetRecoilState(formLayoutItemsState);

  const { data, loading: isLoading } = useRequest<FormData>(async () => {
    const { data: visit } = await query<Visit>({
      type: ModelTypes.visit,
      ids: {
        projectId,
        id: visitId,
        patientId,
      },
    });

    const { data: formTemplates } = await query<FormTemplate[]>({
      type: ModelTypes.formTemplate,
      ids: {
        projectId,
        formTemplateTypeId,
      },
    });

    const { data: form } = await query<FormModel>({
      type: ModelTypes.form,
      ids: {
        visitId,
        id: formId,
      },
    });

    const comments = await getFormsComments({ formId });
    setComments(comments);

    // find the highest version of the form template and status published
    const formTemplate =
      formTemplates.find(el => el.id === form.template?.id) ??
      formTemplates
        ?.filter(el => el.status === FormTemplateStatus.Published)
        .sort((a, b) => b.version - a.version)[0];

    const externalFieldTemplates =
      form.externalFieldTemplates?.filter(f => {
        if (formTemplate?.fieldTemplates.find(t => t.id === f.id)) return false;
        return true;
      }) ?? [];

    setFormsFieldTemplates([
      ...formTemplate?.fieldTemplates,
      ...externalFieldTemplates,
    ]);
    setFormTemplate(formTemplate);

    const fields = form.fields.map(f => {
      if (f.layoutRepeatIndex) {
        return {
          ...f,
          // @ts-ignore
          layoutRepeatIndex: f.layoutRepeatIndex?.split('|').map(Number),
        };
      }
      return f;
    });

    // duplicate layout items
    const itemsWithRepeatedLayoutFields = insertLayoutItemsForRepeatingFields(
      fields,
      formTemplate?.layout.items ?? [],
    );

    // duplicate input layout items
    const itemsWithRepeatedFields = insertItemsForRepeatingFields(
      fields,
      itemsWithRepeatedLayoutFields,
    );

    // duplicate input layout items for repeating fields inside repeating layout fields
    const itemsWithRepeatedLayoutFieldsAndFields =
      insertItemsForRepeatingFieldsInRepeatingLayoutItems(
        fields,
        itemsWithRepeatedFields,
      );

    setLayoutItems(itemsWithRepeatedLayoutFieldsAndFields);
    const initialValues = getInitialFormValues(
      formTemplate?.fieldTemplates,
      fields ?? [],
      visit,
    );

    // this is needed for visibility check when the field is in an other form of the visit
    const externalFieldValues = form.externalFields!.reduce((r, f) => {
      r[f.fieldTemplateId] = f.value;
      return r;
    }, {} as any);

    const importedFieldMap = new Map<string, FieldImportMeta>();
    fields.forEach(f => {
      if (f.importMeta) importedFieldMap.set(f.fieldTemplateId, f.importMeta);
    });

    return {
      visit,
      formTemplate,
      fieldTemplates: formTemplate?.fieldTemplates ?? [],
      initialValues,
      formTemplates,
      formStatus: form.status,
      externalFieldValues,
      importedFieldMap,
    };
  });

  const {
    visit,
    formTemplate,
    fieldTemplates,
    initialValues,
    formStatus,
    externalFieldValues,
    importedFieldMap,
  } = data ?? {};

  return (
    <Paper sx={{ mt: 2, p: 2, position: 'relative' }}>
      <Box
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        p={1}
      >
        <Typography variant="h6" fontWeight={'bold'} sx={{ mr: 1, mt: -0.5 }}>
          {formTempalteType?.name}
        </Typography>
        <LastSavedIndicator date={lastSaved} />
      </Box>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <DndProvider backend={HTML5Backend}>
          <FormLayout
            initialValues={initialValues ?? {}}
            formTemplate={formTemplate}
            fieldTemplates={fieldTemplates ?? []}
            isReadOnly={isReadOnly}
            patient={patient}
            visit={visit}
            mode={mode}
            formStatus={formStatus}
            externalFieldValues={externalFieldValues!}
            importedFieldMap={importedFieldMap}
          />
        </DndProvider>
      )}
    </Paper>
  );
}
